/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

/*
Useful when you are loading markers remotely from server e.g. survey points
*/

import tileLayer from './generic-tile-layer.js';

/*
  Options

	minZoom
	loadingTilesObs / visibleTilesObs: observables for tracking loading/visible tiles
	pointsForTile: function that takes a tile and returns a promise of 'points' for that tile
	the tile parameter has the following properties:
		x, y, z
		removed - true if the tile is no longer visible / valid (check this after promise resolves)
		the returned points can be anything but should have a property called 'id'
	markerForPoint: function that takes a point (see pointsForTile) and returns a google maps marker
*/
function markerLayer(map, options) {
	function increment(obs) { if(obs) obs(obs() + 1); }
	function decrement(obs) { if(obs) obs(obs() - 1); }

	var loadingTiles = 0;
	var visibleTiles = 0;

	// A lookup object containing all visible markers.
	// A reference counting scheme is used to re-use markers over tile loads which minimises visual jarring.
	// Markers with a zero reference count are released at the end of each loading cycle.
	var pointMarkers = {};

	var layer = tileLayer(map, {
		minZoom: options.minZoom || 12,
		addTile: function(tile) {
			increment(options.visibleTilesObs);
			increment(options.loadingTilesObs);
			loadingTiles++;
			visibleTiles++;

			tile.markers = [];

			options.pointsForTile(tile).then(function(points) {
				if(tile.removed) return null;

				points.forEach(function(p) {
					var marker = markerForPoint(p);
					marker.userData.refCount++;
					tile.markers.push(marker);
				});

				return null;
			}).finally(function() {
				decrement(options.loadingTilesObs);
				loadingTiles--;
				if(loadingTiles == 0 || options.progressiveLoading) {
					flushMarkers();
				}
			});
		},
		removeTile: function(tile) {
			decrement(options.visibleTilesObs);
			visibleTiles--;

			tile.markers.forEach(function(marker) {
				marker.userData.refCount--;
			});
			tile.removed = true;

			// Note, tiles are always added then removed, so this should only happen when there are actually no tiles to show
			if(visibleTiles == 0) {
				flushMarkers();
			}
		}
	});

	function markerForPoint(p, doNotCreate) {
		if(!pointMarkers[p.id]) {
			if(doNotCreate) return null;

			var marker = options.markerForPoint(p);

			marker.userData = { refCount: 0 };
			pointMarkers[p.id] = marker;
		}
		return pointMarkers[p.id];
	}

	function flushMarkers() {
		Object.keys(pointMarkers).forEach(function(key) {
			var marker = pointMarkers[key];
			if(marker.userData.refCount == 0) {
				marker.setMap(null);
				delete pointMarkers[key];
			} else if(marker.getMap() == null) {
				marker.setMap(map);
			}
		});
	}

	return {
		refresh: layer.refresh,
		getMarker: function(id) {
			return pointMarkers[id];
		}
	}
}

export default markerLayer;

